import type {
	BillingCycle,
	PlanCode,
	RequestPaymentRequest,
	UpdatePayerInfoRequest
} from '$lib/api/types';

export type BillablePlanCode = Exclude<PlanCode, 'FREE'>;

export interface PayerInfoInput {
	payerBusinessNumber?: string | null;
	payerBusinessName?: string | null;
	payerEmail?: string | null;
	payerPhone?: string | null;
}

export interface CheckoutPaymentInput extends PayerInfoInput {
	plan?: string | null;
	billingCycle?: string | null;
	depositorName?: string | null;
}

type ValidationResult<T> = { ok: true; request: T } | { ok: false; message: string };

const BILLABLE_PLANS = ['BASIC', 'STANDARD', 'PRO'] as const;
const BILLING_CYCLES = ['MONTHLY', 'YEARLY'] as const;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_PATTERN = /^\+?\d{8,15}$/;

export function isBillablePlan(value: string | null | undefined): value is BillablePlanCode {
	return BILLABLE_PLANS.includes(value as BillablePlanCode);
}

export function isBillingCycle(value: string | null | undefined): value is BillingCycle {
	return BILLING_CYCLES.includes(value as BillingCycle);
}

export function parseBillingSelection(
	planParam: string | null,
	cycleParam: string | null
): { plan: BillablePlanCode; billingCycle: BillingCycle; message: string | null } {
	let plan: BillablePlanCode = 'BASIC';
	let billingCycle: BillingCycle = 'MONTHLY';

	if (planParam !== null) {
		if (!isBillablePlan(planParam)) {
			return {
				plan,
				billingCycle,
				message:
					planParam === 'FREE'
						? 'FREE 플랜은 결제가 필요하지 않습니다.'
						: '선택한 요금제를 찾을 수 없습니다.'
			};
		}
		plan = planParam;
	}

	if (cycleParam !== null) {
		if (!isBillingCycle(cycleParam)) {
			return { plan, billingCycle, message: '선택한 결제 주기를 찾을 수 없습니다.' };
		}
		billingCycle = cycleParam;
	}

	return { plan, billingCycle, message: null };
}

export function validatePayerInfoForm(
	input: PayerInfoInput
): ValidationResult<UpdatePayerInfoRequest> {
	const payerBusinessNumber = normalizeBusinessNumber(input.payerBusinessNumber);
	if (payerBusinessNumber === false) {
		return { ok: false, message: '사업자 등록 번호는 000-00-00000 형식으로 입력해주세요.' };
	}

	const payerBusinessName = optionalText(input.payerBusinessName);
	if (payerBusinessName && payerBusinessName.length > 255) {
		return { ok: false, message: '회사명/상호는 255자 이하로 입력해주세요.' };
	}

	const payerEmail = optionalText(input.payerEmail);
	if (payerEmail && !EMAIL_PATTERN.test(payerEmail)) {
		return { ok: false, message: '영수증 받을 이메일 형식이 올바르지 않습니다.' };
	}

	const payerPhone = optionalText(input.payerPhone);
	if (payerPhone && !PHONE_PATTERN.test(payerPhone.replace(/[\s-]/g, ''))) {
		return { ok: false, message: '담당자 연락처는 숫자, 하이픈, 공백만 입력해주세요.' };
	}

	return {
		ok: true,
		request: compactRequest({
			payerBusinessNumber: payerBusinessNumber || undefined,
			payerBusinessName,
			payerEmail,
			payerPhone
		})
	};
}

export function validateCheckoutPaymentForm(
	input: CheckoutPaymentInput
): ValidationResult<RequestPaymentRequest> {
	if (!isBillablePlan(input.plan)) {
		return { ok: false, message: '결제할 요금제를 다시 선택해주세요.' };
	}
	if (!isBillingCycle(input.billingCycle)) {
		return { ok: false, message: '결제 주기를 다시 선택해주세요.' };
	}

	const depositorName = optionalText(input.depositorName);
	if (!depositorName) {
		return { ok: false, message: '입금자명을 입력해주세요.' };
	}
	if (depositorName.length > 100) {
		return { ok: false, message: '입금자명은 100자 이하로 입력해주세요.' };
	}

	const payer = validatePayerInfoForm(input);
	if (!payer.ok) return payer;

	return {
		ok: true,
		request: {
			plan: input.plan,
			billingCycle: input.billingCycle,
			depositorName,
			...payer.request
		}
	};
}

function optionalText(value: string | null | undefined): string | undefined {
	const next = value?.trim();
	return next ? next : undefined;
}

function normalizeBusinessNumber(value: string | null | undefined): string | undefined | false {
	const next = optionalText(value);
	if (!next) return undefined;
	if (/[^0-9\-\s]/.test(next)) return false;

	const digits = next.replace(/\D/g, '');
	if (digits.length !== 10) return false;
	return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function compactRequest(request: UpdatePayerInfoRequest): UpdatePayerInfoRequest {
	return Object.fromEntries(
		Object.entries(request).filter(([, value]) => value !== undefined)
	) as UpdatePayerInfoRequest;
}
